import { AsyncSelect } from '@/components/async-select';
import { Thumb } from '@/components/page';
import { Button, Input } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { money } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { sellableVariantOptions } from '@/lib/options';

/**
 * The lines of an order staff are writing or changing (ADR-062).
 *
 * Rows rather than a table, the way the order page lists what was bought: on a
 * phone the name needs the width, and the amounts sit under it. Only what is
 * wanted travels — a variant and a quantity; the price beside each line is the
 * server's answer (the quote, or the edit's preview), shown as it comes and
 * never computed here (ADR-004). A line already bought says its price is kept.
 */

export interface EditableLine {
  /** Stable for React; the order line's id for a line already bought. */
  key: string;
  itemId?: string;
  variantId: string;
  label: string;
  sku: string;
  /** The options, as the order page prints them beside the SKU: "color: Chestnut brown". */
  detail?: string;
  quantity: number;
  imageUrl?: string | null;
}

export interface PricedLine {
  unitPrice: number;
  lineTotal: number;
}

export function OrderLinesEditor({
  lines,
  priced,
  onChange,
  currency,
  disabled,
}: {
  lines: EditableLine[];
  /** The server's price for each line, by key, when it has answered. */
  priced: Map<string, PricedLine>;
  onChange: (lines: EditableLine[]) => void;
  currency?: string;
  disabled?: boolean;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);

  const setQuantity = (key: string, quantity: number) =>
    onChange(lines.map((line) => (line.key === key ? { ...line, quantity: Math.max(1, Math.min(999, quantity)) } : line)));

  return (
    <div className="flex flex-col gap-3">
      {lines.length > 0 ? (
        <ul className="rounded-control border border-line">
          {lines.map((line) => {
            const price = priced.get(line.key);
            return (
              <li key={line.key} className="flex items-start gap-3 border-b border-line px-4 py-3 last:border-0">
                <Thumb src={mediaUrl(line.imageUrl ?? null)} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <span className="min-w-0 text-sm text-ink">{line.label}</span>
                    <span data-numeric className="whitespace-nowrap text-sm font-medium text-ink">
                      {price ? money(price.lineTotal, locale, currency) : '—'}
                    </span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-xs text-ink-muted">
                    <span className="min-w-0 break-words">
                      <span data-numeric dir="ltr">
                        {line.sku}
                      </span>
                      {line.detail ? ` · ${line.detail}` : ''}
                      {line.itemId ? ` · ${t('priceKept')}` : ''}
                    </span>
                    {price ? (
                      <span data-numeric className="whitespace-nowrap">
                        × {money(price.unitPrice, locale, currency)}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Input
                      type="number"
                      dir="ltr"
                      min={1}
                      max={999}
                      aria-label={`${t('quantity')} · ${line.label}`}
                      className="w-20 text-end"
                      disabled={disabled}
                      value={String(line.quantity)}
                      onChange={(event) => setQuantity(line.key, Number.parseInt(event.target.value, 10) || 1)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      onClick={() => onChange(lines.filter((entry) => entry.key !== line.key))}
                    >
                      {t('remove')}
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="rounded-control bg-surface-sunken px-3 py-2 text-sm text-ink-muted">{t('noLinesYet')}</p>
      )}

      {/* Searched on the server, by SKU, barcode or name — never the whole catalogue.
          Keyed on the lines so it starts empty again after each pick. */}
      <AsyncSelect
        key={lines.map((line) => `${line.key}:${line.quantity}`).join(',')}
        value=""
        emptyLabel={t('addProduct')}
        placeholder={t('searchSkuOrName')}
        disabled={disabled}
        fetchPage={(search, cursor) => sellableVariantOptions.page(search, cursor)}
        onChange={(variantId, option) => {
          if (!variantId || !option) return;
          // The same variant twice is one line with more of it.
          const existing = lines.find((line) => line.variantId === variantId);
          if (existing) {
            setQuantity(existing.key, existing.quantity + 1);
            return;
          }
          onChange([
            ...lines,
            {
              key: `new-${variantId}`,
              variantId,
              label: option.label,
              sku: option.hint?.split(' · ')[0] ?? '',
              quantity: 1,
              imageUrl: option.image ?? null,
            },
          ]);
        }}
      />
    </div>
  );
}
